import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useConfig } from '@/context/ConfigContext';
import type { UserRole } from '@/types';
import { Button } from '@/components/ui/Button';
import { resolveAssetUrl } from '@/utils/assetUrl';
import { TaleemXMark, TaleemXWordmark, isDefaultBranding } from '@/components/brand';

const formSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type FormValues = z.infer<typeof formSchema>;

type LocationState = {
  from?: Location;
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { config } = useConfig();
  const redirectTo = (location.state as LocationState | undefined)?.from?.pathname ?? '/';
  const [error, setError] = useState<string>('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '' },
  });

  const roleDestinations: Record<string, string> = useMemo(
    () => ({
      ADMIN: '/admin',
      TEACHER: '/teacher',
      admin: '/admin',
      teacher: '/teacher'
    }),
    [],
  );

  const onSubmit = handleSubmit(async (values: FormValues) => {
    try {
      setError('');
      console.log('Attempting login with:', values.email);
      await login(values.email, values.password);
      console.log('Login successful, checking cached user...');

      const cached = localStorage.getItem('ams:user');
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as { role: UserRole };
          console.log('Navigating to:', roleDestinations[parsed.role]);
          navigate(roleDestinations[parsed.role], { replace: true });
          return;
        } catch (error) {
          console.warn('Unable to parse cached user after login', error);
        }
      }
      console.log('Navigating to default redirect:', redirectTo);
      navigate(redirectTo, { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      let msg = 'Login failed. Please try again.';

      // Handle Laravel Validation Errors
      if (err.response?.data?.errors) {
        // Grab the first error message from the first field (e.g. email)
        const firstField = Object.keys(err.response.data.errors)[0];
        if (firstField && err.response.data.errors[firstField]) {
          msg = err.response.data.errors[firstField][0];
        }
      } else if (err.response?.data?.message) {
        msg = err.response.data.message;
      } else if (err instanceof Error) {
        msg = err.message;
      }

      setError(msg);
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-3 sm:p-4">
      <div className="w-full max-w-md space-y-6 sm:space-y-8 rounded-xl bg-white p-5 sm:p-8 shadow-lg">
        <div className="mb-4 sm:mb-6 flex flex-col items-center justify-center gap-2 sm:gap-3">
          <div className="inline-flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl bg-white overflow-hidden shadow-sm border border-slate-100 p-2">
            {isDefaultBranding(config) ? (
              <TaleemXMark size="lg" className="h-10 w-10 sm:h-12 sm:w-12" />
            ) : (
              <img
                src={resolveAssetUrl(config?.school_logo_url) || '/logo-mark.svg'}
                alt={config?.school_name || 'School Logo'}
                className="h-full w-full object-contain p-1"
              />
            )}
          </div>
          {isDefaultBranding(config) ? (
            <TaleemXWordmark size="lg" tone="brand" />
          ) : (
            <h2 className="text-lg sm:text-xl font-bold text-brand">{config?.school_name}</h2>
          )}
        </div>

        <div>
          <h1 className="mb-1 text-xl sm:text-2xl font-semibold text-slate-900">Welcome back</h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Sign in with the credentials provided by your school administrator.
          </p>
        </div>
        {error && (
          <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700">
            {error}
          </div>
        )}
        <form className="space-y-5" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-base sm:text-sm min-h-[44px] shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              {...register('email')}
            />
            {errors.email && <p className="text-xs text-rose-500">{errors.email.message}</p>}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-base sm:text-sm min-h-[44px] shadow-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
              {...register('password')}
            />
            {errors.password && <p className="text-xs text-rose-500">{errors.password.message}</p>}
          </div>

          <Button type="submit" className="w-full min-h-[44px]" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  );
}
